// src/components/FinancialIntelligence/FinancialIntelligenceSection.js
//
// Top-level Financial Intelligence section component.
// Orchestrates: thread history panel, output canvas, context bar,
// situation chips, and input bar.
//
// Layout: two-panel (thread history left ~280px, output canvas right)
// Client selector state is shared from ClientDashboard via selectedClientId prop.

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Divider, Collapse, Alert, IconButton } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import FIThreadPanel from './FIThreadPanel';
import FIContextBar from './FIContextBar';
import FIBottomPanel from './FIBottomPanel';
import FIInputBar from './FIInputBar';
import FIOutputCanvas from './FIOutputCanvas';

const FinancialIntelligenceSection = ({
  user,
  userData,
  clients,
  selectedClientId,
  onClientSelect,
  clientIsActive,
}) => {
  // Selected thread entry
  const [activeEntryId, setActiveEntryId]   = useState(null);
  const [activeEntry, setActiveEntry]        = useState(null);

  // Streaming state (mirrors QuestionsSection pattern)
  const [streamingEntryId, setStreamingEntryId]   = useState(null);
  const [streamingText, setStreamingText]         = useState('');
  const [streamingPhase, setStreamingPhase]       = useState(null);
  const [followUpSuggestions, setFollowUpSuggestions] = useState([]);

  // Auto-scroll: ref to the output scroll container; userScrolledUpRef tracks
  // whether the user manually scrolled up — stored as ref (no re-render needed).
  const scrollContainerRef = useRef(null);
  const userScrolledUpRef  = useRef(false);

  // Input state (lifted so situation chips can pre-fill)
  const [inputText, setInputText]         = useState('');
  const [pendingSituationKey, setPendingSituationKey] = useState(null);

        // Filter panel state — drives FIBottomPanel and FIThreadPanel
  const [filterPanelOpen, setFilterPanelOpen]       = useState(false);
  const [filterSearchQuery, setFilterSearchQuery]   = useState('');
  const [filterSortOption, setFilterSortOption]     = useState('newest');

  const getDefaultDateFrom = () => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().split('T')[0];
  };

  const [filterDateFrom, setFilterDateFrom]         = useState('');
  const [filterDateTo, setFilterDateTo]             = useState('');
  const [showAllHistory, setShowAllHistory]         = useState(false);
  const [filteredCount, setFilteredCount]           = useState(0);
  const [showFIInfo, setShowFIInfo]                 = useState(false);

  // Derive selected client object
  const selectedClient = clients?.find(c => c.id === selectedClientId) || null;

  // ── Stream handlers ───────────────────────────────────────────
   const handleStreamStart = useCallback(() => {
    userScrolledUpRef.current = false;  // reset scroll lock on new stream
    setFilterPanelOpen(false);
    setStreamingEntryId('pending');
    setStreamingText('');
    setStreamingPhase('assembling_context');
    setActiveEntryId(null);
    setActiveEntry(null);
    setFollowUpSuggestions([]);
  }, []);

  const handleStreamChunk = useCallback((text) => {
    setStreamingText(prev => prev + text);
  }, []);

  const handleStreamPhase = useCallback((phase) => {
    setStreamingPhase(phase);
  }, []);

  const handleStreamDone = useCallback((entryId, suggestions) => {
    setStreamingPhase('done');
    setStreamingEntryId(entryId);
    setFollowUpSuggestions(suggestions || []);
    // FIThreadPanel's Firestore listener will pick up the new entry automatically.
    // Select it once it appears via handleEntrySelect from the panel.
  }, []);

  const handleStreamError = useCallback(() => {
    setStreamingEntryId(null);
    setStreamingText('');
    setStreamingPhase(null);
  }, []);

  // Called by FIThreadPanel when a new entry appears that matches streamingEntryId
  const handleStreamingEntryPersisted = useCallback((entryId) => {
    if (streamingEntryId === entryId || streamingEntryId === 'pending') {
      setStreamingEntryId(null);
      setStreamingText('');
      setStreamingPhase(null);
    }
  }, [streamingEntryId]);

  // Reset active entry and filter state when client changes
    useEffect(() => {
    setActiveEntryId(null);
    setActiveEntry(null);
    setStreamingText('');
    setStreamingPhase(null);
    setStreamingEntryId(null);
    setFollowUpSuggestions([]);
    setFilterPanelOpen(false);
    setFilterSearchQuery('');
    setFilterSortOption('newest');
    setFilterDateFrom('');
    setFilterDateTo('');
    setShowAllHistory(false);
    setFilteredCount(0);
  }, [selectedClientId]);

  // ── Entry selection ───────────────────────────────────────────
  const handleEntrySelect = useCallback((entry) => {
    setActiveEntryId(entry.id);
    setActiveEntry(entry);
    // Only clear streaming state when this selected entry is the actual
    // persisted streamed entry. Do NOT clear while streamingEntryId is still
    // 'pending', or clicking an older history item will wipe the live stream.
    if (entry.id === streamingEntryId) {
      handleStreamingEntryPersisted(entry.id);
    }
  }, [streamingEntryId, handleStreamingEntryPersisted]);

  // ── Situation chip pre-fill ───────────────────────────────────
  const handleSituationSelect = useCallback((promptText, situationKey) => {
    setInputText(promptText);
    setPendingSituationKey(situationKey);
  }, []);

  // ── Follow-up chip click ──────────────────────────────────────
  const handleFollowUpClick = useCallback((suggestion) => {
    setInputText(suggestion);
    setPendingSituationKey(null);
  }, []);

  // ── After submission: reset situation key ─────────────────────
  const handleSubmitComplete = useCallback(() => {
    setInputText('');
    setPendingSituationKey(null);
  }, []);

  // ── Auto-scroll: scroll to bottom during streaming unless user has scrolled up ──
  const handleOutputScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    userScrolledUpRef.current = distanceFromBottom > 60;
  }, []);

  useEffect(() => {
    if (!streamingText || userScrolledUpRef.current) return;
    const el = scrollContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [streamingText]);

  // ── No clients guard ──────────────────────────────────────────
  if (!clients || clients.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No clients yet. Add a client in Manage Clients to get started.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>

            {/* ── Client Selector ── */}
      <Box sx={{ px: 2, pt: 1, pb: 1.5, flexShrink: 0 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Client</InputLabel>
            <Select
              value={selectedClientId || ''}
              label="Client"
              onChange={(e) => onClientSelect(e.target.value)}
            >
              {clients.filter(c => c.isActive !== false).map(c => (
                <MenuItem key={c.id} value={c.id}>
                  {c.displayName || c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton size="small" onClick={() => setShowFIInfo(prev => !prev)}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Box>

        <Collapse in={showFIInfo}>
          <Alert severity="info" sx={{ mt: 1.5 }} onClose={() => setShowFIInfo(false)}>
             <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Q&A vs. Financial Intelligence:</strong> These are complementary tools with different jobs. Use FI when the question is specifically financial and you want a structured, exportable output. Use Q&A when you want a conversational response to any business or strategic question — FI and Q&A both use client financial data, but Q&A handles the full range of strategic, operational, and advisory questions across the engagement.
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>How to use it:</strong> Open the panel from the icon to the left of the input to start with a structured report template — or type any financial question directly. Report templates are designed to be customized: the prompt that fills the input is yours to edit before running.
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Edge remembers context:</strong> Every analysis you run is informed by the client's master summary, prior financial analyses, Q&A history, and business updates. You never have to re-explain the client situation — Edge carries it forward automatically.
            </Typography>
            <Typography variant="body2">
              <strong>Analysis history:</strong> Every completed analysis is saved in the left panel. Use search, sort, and date filters to navigate your work across a client engagement.
            </Typography>
          </Alert>
        </Collapse>
      </Box>
      <Divider />

      {/* ── Two-panel body ── */}
      <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>

        {/* Left: Thread History Panel */}
        <Box sx={{
          width: 280,
          flexShrink: 0,
          borderRight: '1px solid',
          borderColor: 'divider',
          overflowY: 'auto',
          bgcolor: 'grey.50',
        }}>
                    <FIThreadPanel
            clientId={selectedClientId}
            user={user}
            activeEntryId={activeEntryId}
            streamingEntryId={streamingEntryId}
            onEntrySelect={handleEntrySelect}
            onStreamingEntryPersisted={handleStreamingEntryPersisted}
            searchQuery={filterSearchQuery}
            sortOption={filterSortOption}
            dateFrom={showAllHistory ? '' : (filterDateFrom || getDefaultDateFrom())}
            dateTo={showAllHistory ? '' : filterDateTo}
            onFilteredCountChange={setFilteredCount}
            onClearFilters={() => {
              setFilterSearchQuery('');
              setFilterDateFrom('');
              setFilterDateTo('');
              setShowAllHistory(false);
            }}
            showAllHistory={showAllHistory}
            onShowAllHistory={() => {
              setShowAllHistory(true);
              setFilterDateFrom('');
              setFilterDateTo('');
            }}
            hasExplicitFilters={!!(filterSearchQuery || filterDateFrom || filterDateTo)}
          />
        </Box>

        {/* Right: Output Canvas + Context Bar + Input */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>

          {/* Context Bar */}
          <FIContextBar
            client={selectedClient}
            clientId={selectedClientId}
            user={user}
          />

          <Divider />

          {/* Output Canvas — scrollable */}
          <Box
            ref={scrollContainerRef}
            onScroll={handleOutputScroll}
            sx={{ flexGrow: 1, overflowY: 'auto', px: 3, py: 2 }}
          >
                        <FIOutputCanvas
              activeEntry={activeEntry}
              streamingText={streamingText}
              streamingPhase={streamingPhase}
              streamingPrompt={inputText}
              followUpSuggestions={followUpSuggestions}
              onFollowUpClick={handleFollowUpClick}
              clientId={selectedClientId}
              client={selectedClient}
              user={user}
            />
          </Box>

          {/* Input area — pinned at bottom */}
<Box
  sx={{
    flexShrink: 0,
    borderTop: '1px solid',
    borderColor: 'divider',
    pt: 1,
    pb: 1.5,
    px: 2,
    position: 'relative',
    overflow: 'visible',
    zIndex: 2,
  }}
>
                        <FIBottomPanel
              open={filterPanelOpen}
              onTemplateSelect={(prompt, key) => {
                handleSituationSelect(prompt, key);
                setFilterPanelOpen(false);
              }}
              searchQuery={filterSearchQuery}
              onSearchChange={setFilterSearchQuery}
              sortOption={filterSortOption}
              onSortChange={setFilterSortOption}
              dateFrom={filterDateFrom}
              dateTo={filterDateTo}
              onDateFromChange={(val) => {
                setFilterDateFrom(val);
                setShowAllHistory(false);
              }}
              onDateToChange={(val) => {
                setFilterDateTo(val);
                setShowAllHistory(false);
              }}
              onClearDates={() => {
                setFilterDateFrom('');
                setFilterDateTo('');
                setShowAllHistory(false);
              }}
              showAllHistory={showAllHistory}
              onToggleShowAllHistory={() => {
                setShowAllHistory(prev => {
                  const next = !prev;
                  if (next) {
                    setFilterDateFrom('');
                    setFilterDateTo('');
                  }
                  return next;
                });
              }}
               filteredCount={filteredCount}
              user={user}
              currentPrompt={inputText}
              disabled={!selectedClientId || !clientIsActive || !!streamingPhase}
            />
            <FIInputBar
              clientId={selectedClientId}
              client={selectedClient}
              clientIsActive={clientIsActive}
              clients={clients}
              user={user}
              value={inputText}
              onChange={setInputText}
              situationKey={pendingSituationKey}
              isStreaming={!!streamingPhase}
              onSubmitComplete={handleSubmitComplete}
              onStreamStart={handleStreamStart}
              onStreamChunk={handleStreamChunk}
              onStreamPhase={handleStreamPhase}
              onStreamDone={handleStreamDone}
              onStreamError={handleStreamError}
              onToggleFilter={() => setFilterPanelOpen(prev => !prev)}
              filterOpen={filterPanelOpen}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FinancialIntelligenceSection;